import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import StatusBadge, { STATUS_META } from "@/components/StatusBadge";
import { SERVICE_BY_KEY } from "@/lib/services";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, ChatCircleDots, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIMELINE = ["pending", "approved", "in_progress", "completed"];

export default function ProjectDetails() {
  const { id } = useParams();
  const nav = useNavigate();
  const [project, setProject] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get(`/projects/${id}`).then(({ data }) => setProject(data)),
      api.get(`/invoices`).then(({ data }) => setInvoices(data.filter(i => i.project_id === id))),
    ]).catch(() => toast.error("Project not found")).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <AppShell><div className="p-16 text-center text-neutral-500">Loading…</div></AppShell>;
  if (!project) return <AppShell><div className="p-16 text-center">Not found</div></AppShell>;

  const svc = SERVICE_BY_KEY[project.service_type];
  const currentIdx = TIMELINE.indexOf(project.status);
  const isRejected = project.status === "rejected";

  return (
    <AppShell
      subtitle={`${svc?.code} · ${svc?.name}`}
      title={project.title}
      actions={
        <>
          <Button variant="outline" onClick={() => nav("/dashboard/projects")} className="rounded-md h-10 border-black/20" data-testid="pd-back-btn">
            <ArrowLeft size={14} weight="bold" className="mr-2" /> All projects
          </Button>
          <Button onClick={() => nav(`/dashboard/chat?project_id=${encodeURIComponent(project.id)}`)} className="fx-btn-primary rounded-md h-10" data-testid="pd-chat-btn">
            <ChatCircleDots size={14} weight="bold" className="mr-2" /> Discuss with Agenaix Assistant
          </Button>
        </>
      }
    >
      <div className="mb-6"><StatusBadge status={project.status} testid="pd-status-badge" /></div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-white border border-black/10 rounded-md h-auto p-1">
          <TabsTrigger value="overview" className="fx-mono-tag data-[state=active]:bg-black data-[state=active]:text-white rounded-md px-4 py-2" data-testid="pd-tab-overview">OVERVIEW</TabsTrigger>
          <TabsTrigger value="timeline" className="fx-mono-tag data-[state=active]:bg-black data-[state=active]:text-white rounded-md px-4 py-2" data-testid="pd-tab-timeline">PIPELINE</TabsTrigger>
          <TabsTrigger value="invoices" className="fx-mono-tag data-[state=active]:bg-black data-[state=active]:text-white rounded-md px-4 py-2" data-testid="pd-tab-invoices">INVOICES {invoices.length > 0 && <span className="ml-1.5 font-mono text-xs opacity-70">({invoices.length})</span>}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-4">
              <div className="border border-black/10 p-8">
                <div className="fx-mono-tag mb-3">DESCRIPTION</div>
                <p className="text-base text-neutral-800 leading-relaxed whitespace-pre-wrap" data-testid="pd-description">{project.description}</p>
              </div>
              {project.admin_note && (
                <div className="border border-black/10 p-8" style={{background:'#F4F6F8'}} data-testid="pd-admin-note">
                  <div className="fx-mono-tag mb-3" style={{color:'#0B4DCC'}}>NOTE FROM AGENAIX</div>
                  <p className="text-base text-neutral-800 leading-relaxed whitespace-pre-wrap">{project.admin_note}</p>
                </div>
              )}
            </div>
            <aside className="border border-black/10 p-6 h-fit">
              <div className="fx-mono-tag mb-4">META</div>
              <dl className="space-y-4 text-sm">
                <div><dt className="text-neutral-500 text-xs uppercase tracking-wider">Project ID</dt><dd className="font-mono">{project.id.slice(0,8)}</dd></div>
                <div><dt className="text-neutral-500 text-xs uppercase tracking-wider">Service</dt><dd>{svc?.name}</dd></div>
                <div><dt className="text-neutral-500 text-xs uppercase tracking-wider">Budget</dt><dd>{project.budget || "—"}</dd></div>
                <div><dt className="text-neutral-500 text-xs uppercase tracking-wider">Timeline</dt><dd>{project.timeline || "—"}</dd></div>
                <div><dt className="text-neutral-500 text-xs uppercase tracking-wider">Submitted</dt><dd dir="ltr">{formatDateTime(project.created_at)}</dd></div>
                <div><dt className="text-neutral-500 text-xs uppercase tracking-wider">Updated</dt><dd dir="ltr">{formatDateTime(project.updated_at)}</dd></div>
              </dl>
            </aside>
          </div>
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <div className="border border-black/10 p-8">
            <div className="fx-mono-tag mb-6 flex items-center gap-2"><ClockCounterClockwise size={12} /> DELIVERY PIPELINE</div>
            {isRejected ? (
              <div className="border p-6 rounded-md" style={{borderColor: STATUS_META.rejected.bd, background: STATUS_META.rejected.bg, color: STATUS_META.rejected.fg}}>
                <div className="font-semibold">This project was declined.</div>
                <div className="text-sm mt-1 opacity-80">See the note above for context, or start a new brief.</div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-black/10 border border-black/10">
                {TIMELINE.map((s, i) => {
                  const done = i <= currentIdx;
                  const active = i === currentIdx;
                  return (
                    <div key={s} className="bg-white p-6" data-testid={`pd-timeline-${s}`}>
                      <div className="fx-mono-tag" style={{color: done ? '#0B4DCC' : '#a3a3a3'}}>STEP {String(i+1).padStart(2,'0')}</div>
                      <div className={`mt-3 text-lg font-semibold tracking-tight ${done ? '' : 'text-neutral-400'}`}>{STATUS_META[s].label}</div>
                      <div className="mt-3 h-1 w-full" style={{background: done ? (active ? '#0B4DCC' : '#0B1F3A') : '#EBEBEB'}} />
                      {active && <div className="mt-3 text-xs text-[#0B4DCC] font-mono">◆ CURRENT STAGE</div>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="invoices" className="mt-6">
          {invoices.length === 0 ? (
            <div className="border border-dashed border-black/20 p-12 text-center text-neutral-500">
              <div className="fx-mono-tag mb-2">NO INVOICES</div>
              <div>Invoices are generated automatically once a project is approved.</div>
            </div>
          ) : (
            <div className="border border-black/10">
              {invoices.map(inv => (
                <div key={inv.id} className="p-5 border-b border-black/5 last:border-b-0 flex items-center justify-between" data-testid={`pd-invoice-${inv.id}`}>
                  <div>
                    <div className="font-mono text-sm font-semibold">{inv.number}</div>
                    <div className="text-xs text-neutral-500 mt-1" dir="ltr">Issued {formatDate(inv.issued_at)} · Due {formatDate(inv.due_at)}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-lg font-bold">{formatCurrency(inv.amount_usd, inv.currency)}</div>
                    <span className={`fx-badge mt-1 inline-block ${inv.status === "paid" ? "" : ""}`} style={{
                      background: inv.status === "paid" ? "#E4F5EA" : "#FFF7D6",
                      color: inv.status === "paid" ? "#1E7C3B" : "#8A6A00",
                      borderColor: inv.status === "paid" ? "#34C759" : "#FFCC00",
                    }}>{inv.status.toUpperCase()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}
